/**
 * HTTP service for the Document Library feature (MYD-282).
 *
 * Thin axios wrapper — keeps the slice's thunks free of URL strings
 * and request shaping. Each function takes an `axios` config object
 * (headers, etc.) so the caller can compose auth and per-request
 * options without this module knowing about Redux state.
 */
import axios, { AxiosProgressEvent, AxiosRequestConfig } from 'axios'
import type {
	CreateShareLinkResponse,
	DocumentShareLink,
	InlineUrlsResponse,
	LibraryDocument,
	LibraryDocumentVersion,
	LibraryScopeArgs,
	RevokeShareLinkResponse,
} from '../types/documentLibrary.types'

/**
 * Translate a `LibraryScopeArgs` discriminated union into the
 * `{ scope, project?, portfolio? }` wire shape both the list query
 * params and the create multipart body accept. Centralised here so the
 * slice / hooks / dialogs never need to know the wire format.
 */
const scopeArgsToWireFields = (
	args: LibraryScopeArgs,
): {
	scope: 'project' | 'portfolio' | 'location' | 'global' | 'my-library'
	project?: string
	portfolio?: string
	location?: string
} => {
	switch (args.scope) {
		case 'project':
			return { scope: 'project', project: args.projectId }
		case 'portfolio':
			return { scope: 'portfolio', portfolio: args.portfolioId }
		case 'location':
			return { scope: 'location', location: args.locationId }
		case 'global':
			return { scope: 'global' }
		case 'my-library':
			// userId on the client args is for caching identity only;
			// the backend resolves the access set from req.user._id.
			return { scope: 'my-library' }
	}
}

const BASE = '/api/library-documents'
const SHARE_BASE = '/api/document-shares'

/** Build the per-document download URL the controller redirects from. */
export const buildDownloadUrl = (id: string, versionId?: string): string =>
	versionId
		? `${BASE}/${id}/versions/${versionId}/download`
		: `${BASE}/${id}/download`

/**
 * Authenticated download flow. axios follows the backend's 302 to a
 * presigned R2 URL; the browser strips Authorization on the cross-
 * origin redirect (R2 uses the URL signature instead). The file lands
 * in JS memory as a Blob, then we trigger the save dialog. Acceptable
 * for typical Office docs (1–50 MB); the 500 MB upload ceiling could
 * stress the heap and is a known v1 trade-off.
 */
export const downloadAndSave = async (
	{ id, versionId, filename }: { id: string; versionId?: string; filename: string },
	config: AxiosRequestConfig = {},
): Promise<void> => {
	const url = buildDownloadUrl(id, versionId)
	const { data } = await axios.get<Blob>(url, {
		...config,
		responseType: 'blob',
	})
	const objectUrl = URL.createObjectURL(data)
	try {
		const a = document.createElement('a')
		a.href = objectUrl
		a.download = filename || 'download'
		document.body.appendChild(a)
		a.click()
		a.remove()
	} finally {
		setTimeout(() => URL.revokeObjectURL(objectUrl), 1000)
	}
}

const onProgressFor = (
	onProgress: ((percent: number) => void) | undefined,
): AxiosRequestConfig['onUploadProgress'] | undefined =>
	onProgress
		? (event: AxiosProgressEvent) => {
				if (!event.total) return
				const percent = Math.round((event.loaded * 100) / event.total)
				onProgress(percent)
		  }
		: undefined

export const listForScope = async (
	scopeArgs: LibraryScopeArgs,
	config: AxiosRequestConfig = {},
): Promise<LibraryDocument[]> => {
	const { data } = await axios.get<LibraryDocument[]>(BASE, {
		...config,
		params: { ...(config.params || {}), ...scopeArgsToWireFields(scopeArgs) },
	})
	return data
}

export const getOne = async (
	id: string,
	config: AxiosRequestConfig = {},
): Promise<LibraryDocument> => {
	const { data } = await axios.get<LibraryDocument>(`${BASE}/${id}`, config)
	return data
}

export const listVersions = async (
	id: string,
	config: AxiosRequestConfig = {},
): Promise<LibraryDocumentVersion[]> => {
	const { data } = await axios.get<LibraryDocumentVersion[]>(
		`${BASE}/${id}/versions`,
		config,
	)
	return data
}

export const getInlineUrls = async (
	id: string,
	config: AxiosRequestConfig = {},
): Promise<InlineUrlsResponse> => {
	const { data } = await axios.get<InlineUrlsResponse>(
		`${BASE}/${id}/inline-url`,
		config,
	)
	return data
}

export const renameDocument = async (
	{ id, libraryName }: { id: string; libraryName: string },
	config: AxiosRequestConfig = {},
): Promise<LibraryDocument> => {
	const { data } = await axios.patch<LibraryDocument>(
		`${BASE}/${id}`,
		{ libraryName },
		config,
	)
	return data
}

export interface UploadDocumentArgs {
	scopeArgs: LibraryScopeArgs
	file: File
	libraryName?: string
	changeNote?: string
	onProgress?: (percent: number) => void
}

export const uploadDocument = async (
	{ scopeArgs, file, libraryName, changeNote, onProgress }: UploadDocumentArgs,
	config: AxiosRequestConfig = {},
): Promise<LibraryDocument> => {
	const form = new FormData()
	const wire = scopeArgsToWireFields(scopeArgs)
	form.append('scope', wire.scope)
	// Append whichever foreign key the scope carries, by iterating the wire
	// object rather than naming each field. The previous version hand-listed
	// `project` and `portfolio` and silently omitted `location`, so every
	// location upload was rejected with "scope=location requires the location
	// field" while the list call, which spreads the same object, worked fine.
	// Iterating means a new scope cannot be forgotten here again.
	for (const [key, value] of Object.entries(wire)) {
		if (key !== 'scope' && value) form.append(key, value)
	}
	if (libraryName) form.append('libraryName', libraryName)
	if (changeNote) form.append('changeNote', changeNote)
	form.append('file', file)
	const { data } = await axios.post<LibraryDocument>(BASE, form, {
		...config,
		onUploadProgress: onProgressFor(onProgress),
	})
	return data
}

export interface UploadVersionArgs {
	id: string
	file: File
	changeNote: string
	onProgress?: (percent: number) => void
}

export const uploadVersion = async (
	{ id, file, changeNote, onProgress }: UploadVersionArgs,
	config: AxiosRequestConfig = {},
): Promise<LibraryDocument> => {
	const form = new FormData()
	form.append('changeNote', changeNote)
	form.append('file', file)
	const { data } = await axios.post<LibraryDocument>(
		`${BASE}/${id}/versions`,
		form,
		{ ...config, onUploadProgress: onProgressFor(onProgress) },
	)
	return data
}

export const softDeleteDocument = async (
	id: string,
	config: AxiosRequestConfig = {},
): Promise<{ id: string; isDeleted: boolean }> => {
	const { data } = await axios.put(`${BASE}/${id}/delete`, {}, config)
	return data
}

export const softDeleteVersion = async (
	{ id, versionId }: { id: string; versionId: string },
	config: AxiosRequestConfig = {},
): Promise<{ id: string; isDeleted: boolean }> => {
	const { data } = await axios.put(
		`${BASE}/${id}/versions/${versionId}/delete`,
		{},
		config,
	)
	return data
}

export interface CreateShareLinkArgs {
	documentId: string
	versionId?: string
	ttlDays?: number
	email?: string
	message?: string
}

export const createShareLink = async (
	{ documentId, versionId, ttlDays, email, message }: CreateShareLinkArgs,
	config: AxiosRequestConfig = {},
): Promise<CreateShareLinkResponse> => {
	const { data } = await axios.post<CreateShareLinkResponse>(
		SHARE_BASE,
		{ documentId, versionId, ttlDays, email, message },
		config,
	)
	return data
}

export const revokeShareLink = async (
	token: string,
	config: AxiosRequestConfig = {},
): Promise<RevokeShareLinkResponse> => {
	const { data } = await axios.put<RevokeShareLinkResponse>(
		`${SHARE_BASE}/${token}/revoke`,
		{},
		config,
	)
	return data
}

export const listShareLinks = async (
	documentId: string,
	config: AxiosRequestConfig = {},
): Promise<DocumentShareLink[]> => {
	const { data } = await axios.get<DocumentShareLink[]>(SHARE_BASE, {
		...config,
		params: { ...(config.params || {}), documentId },
	})
	return data
}
